import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';

// Property Data Interfaces
export interface PropertySummary {
  id: number;
  property_id: string | null;
  property_name: string | null;
  property_type: string | null;
  star_rating: string | null;
  scale: string | null;
  brand: string | null;
  property_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
  rooms: number | null;
  year_built: number | null;
  operational_status: string | null;
  hotel_operator: string | null;
  owner_name: string | null;
  market_name: string | null;
  submarket_name: string | null;
  market_display_name?: string | null;
  country_name?: string | null;
  continent_name?: string | null;
}

export interface PropertyLocation {
  property_address: string | null;
  cross_street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  county_name: string | null;
  latitude: number | null;
  longitude: number | null;
  property_location: string | null;
  hotel_location_type: string | null;
  secondary_type: string | null;
}

export interface PropertyMarket {
  id: number;
  name: string | null;
  submarket_name: string | null;
  submarket_cluster: string | null;
  country: {
    id: number;
    name: string | null;
    code: string | null;
  };
  continent: string | null;
  subcontinent: string | null;
}

export interface PropertyDetails {
  rooms: number | null;
  year_built: number | null;
  month_built: number | null;
  year_renovated: number | null;
  month_renovated: number | null;
  hotel_open_date: string | null;
  operational_status: string | null;
  operation_type: string | null;
  building_status: string | null;
  building_class: string | null;
  constr_status: string | null;
  all_inclusive: string | null;
  all_suites: string | null;
}

export interface PropertyPhysical {
  number_of_stories: number | null;
  number_of_floors: number | null;
  number_of_parking_spaces: number | null;
  parking_spaces_per_room: number | null;
  parking_ratio: number | null;
  rba: number | null;
  land_area_ac: number | null;
  land_area_sf: number | null;
  number_of_elevators: number | null;
}

export interface PropertyOwnership {
  hotel_operator: string | null;
  owner_name: string | null;
  owner_address: string | null;
  owner_city_state_zip: string | null;
  owner_contact: string | null;
  owner_phone: string | null;
  true_owner: string | null;
  true_owner_name: string | null;
  property_manager_name: string | null;
  fund: string | null;
  fund_name: string | null;
}

export interface PropertyMeetingSpace {
  mtg_rooms: number | null;
  total_mtg_space: number | null;
  max_contig_mtg_space: number | null;
}

export interface PropertyFinancial {
  for_sale_status: string | null;
  for_sale_price: number | null;
  for_sale_price_per_room: number | null;
  last_sale_date: string | null;
  last_sale_price: number | null;
  days_on_market: number | null;
  cap_rate: number | null;
  loan_amount: number | null;
  loan_type: string | null;
  interest_rate: number | null;
  interest_rate_type: string | null;
  origination_date: string | null;
  maturity_date: string | null;
}

export interface PropertyAmenities {
  amenities: string | null;
  services: string | null;
  features: string | null;
  restaurant: string | null;
  sprinklers: string | null;
  leed_certified: string | null;
  energy_star: string | null;
}

export interface PropertyDemographics {
  population_age: {
    avg_2024: number | null;
    median_2024: number | null;
    working_age_2024: number | null;
    senior_2024: number | null;
  };
  household_age: {
    median_2024: number | null;
    prime_working_2024: number | null;
  };
}

export interface PropertyMetadata {
  import_date: string | null;
  data_participation: string | null;
}

export interface PropertyFull {
  id: number;
  property_id: string | null;
  property_name: string | null;
  property_type: string | null;
  star_rating: string | null;
  scale: string | null;
  brand: string | null;
  parent_company: string | null;
  hotel_class: string | null;
  hotel_grade: string | null;
  location: PropertyLocation;
  market: PropertyMarket;
  details: PropertyDetails;
  physical: PropertyPhysical;
  ownership: PropertyOwnership;
  meeting_space: PropertyMeetingSpace;
  financial: PropertyFinancial;
  amenities: PropertyAmenities;
  demographics: PropertyDemographics | null;
  metadata: PropertyMetadata;
}

export interface PropertiesResponse {
  market: {
    id: number;
    name: string | null;
    country: string | null;
    continent: string | null;
  };
  properties: PropertySummary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface PropertyResponse {
  property: PropertyFull;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Hook to fetch properties by market
export const useMarketProperties = (
  marketId: number | undefined,
  options?: {
    limit?: number;
    offset?: number;
    propertyType?: string;
  }
) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<PropertiesResponse, Error>({
    queryKey: ['marketProperties', marketId, options],
    queryFn: async (): Promise<PropertiesResponse> => {
      try {
        if (accounts.length === 0 || !marketId) {
          throw new Error('Not authenticated or no market ID provided');
        }

        const params = new URLSearchParams();
        if (options?.limit) params.append('limit', options.limit.toString());
        if (options?.offset) params.append('offset', options.offset.toString());
        if (options?.propertyType) params.append('property_type', options.propertyType);

        const url = `${API_URL}/api/market-properties/markets/${marketId}/properties${
          params.toString() ? `?${params.toString()}` : ''
        }`;
        
        const response = await authenticatedFetch(url, {
          timeout: 30000 // 30 seconds timeout
        });
        
        if (!response) {
          throw new Error('Failed to fetch market properties: No response from authenticatedFetch');
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status} ${response.statusText}`);
        }

        const result: ApiResponse<PropertiesResponse> = await response.json();
        
        if (!result.success || !result.data) {
          throw new Error(result.message || 'Failed to fetch properties');
        }
        
        return result.data;
      } catch (error) {
        console.error('Market properties fetch error:', error);
        throw error;
      }
    },
    enabled: accounts.length > 0 && !!marketId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

// Hook to fetch property by ID
export const usePropertyById = (
  propertyId: string | undefined
) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<PropertyFull, Error>({
    queryKey: ['property', propertyId],
    queryFn: async (): Promise<PropertyFull> => {
      try {
        if (accounts.length === 0 || !propertyId) {
          throw new Error('Not authenticated or no property ID provided');
        }

        const url = `${API_URL}/api/market-properties/properties/${propertyId}`;
        
        const response = await authenticatedFetch(url, {
          timeout: 30000 // 30 seconds timeout
        });
        
        if (!response) {
          throw new Error('Failed to fetch property: No response from authenticatedFetch');
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status} ${response.statusText}`);
        }

        const result: ApiResponse<PropertyResponse> = await response.json();
        
        if (!result.success || !result.data?.property) {
          throw new Error(result.message || 'Failed to fetch property');
        }
        
        return result.data.property;
      } catch (error) {
        console.error('Property fetch error:', error);
        throw error;
      }
    },
    enabled: accounts.length > 0 && !!propertyId,
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};